import { createAsyncThunk } from "@reduxjs/toolkit";
import AuthService from "../../services/AuthServices";
import { IRegistrationData, SendToEnum } from "../../types/types";
import {
  SetErrors,
  SetIsForeignPhone,
  SetIsLoading,
  SetReqToken,
  SetS3Token,
  SetSendedTo,
  SetSendedToValue,
  SetToken,
} from "../slices/registrationSlice";
import { message } from "antd";
import { ErrorIcon } from "../../assets/icons/ErrorIcon";
import { SuccessIcon } from "../../assets/icons/SuccessIcon";
import { errorsKey, maskEmail, redirect } from "../../utils";
import { tabsNamesKeys } from "../../pages/Registration/Registration.models";
import { setUserTokens } from "./userAction";

const onMessageErrorKey = (err: number) => {
  const err_text = errorsKey(err);
  message.error({
    className: "messageError",
    content: err_text,
    icon: <ErrorIcon />,
  });
};

const onMessageError = (err: string) => {
  message.error({
    className: "messageError",
    content: err,
    icon: <ErrorIcon />,
  });
};

const onMessageSuccess = (messageText: string) => {
  message.success({
    className: "messageSuccess",
    content: messageText,
    icon: <SuccessIcon />,
  });
};

interface IDefaultProps {
  setStep(step: tabsNamesKeys): void;
}

interface IConfirmCodeProps extends IDefaultProps {
  code: string;
}

export const signupByPhone = createAsyncThunk<void, IDefaultProps>(
  "registrationSlice/signupByPhone",
  async ({ setStep }, { dispatch, getState }) => {
    const { registrationSlice } = getState() as IRegistrationData;
    const { phone, verify_token } = registrationSlice;

    if (!phone) {
      onMessageError("Для регистрации требуется адрес электронной почты.");
      dispatch(SetErrors(["Для регистрации необходим номер телефона."]));
      return;
    }

    if (!verify_token) {
      onMessageError("Для регистрации требуется токен подтверждения.");
      dispatch(SetErrors(["Для регистрации требуется токен подтверждения."]));
      return;
    }

    dispatch(SetErrors([]));
    dispatch(SetIsLoading(true));
    dispatch(SetIsForeignPhone(false));
    dispatch(SetSendedTo(SendToEnum.phone));

    const response = await AuthService.$signup({
      phone,
      verify_token,
      send_to: SendToEnum.phone,
    });
    const errorKey = response.data.errors[0]?.key;

    if (response.data.status) {
      onMessageSuccess("Код подтверждения отправлен на ваш номер телефона.");

      const { sended_to, reg_token } = response.data.values[0];

      dispatch(SetReqToken(reg_token));
      dispatch(SetSendedToValue(sended_to));
      dispatch(SetIsLoading(false));

      setStep(tabsNamesKeys.confirmCode);
    } else if (errorKey === 280) {
      dispatch(SetIsForeignPhone(true));
      dispatch(SetIsLoading(false));
      onMessageError("Для регистрации требуется адрес электронной почты.");

      setStep(tabsNamesKeys.email);
    } else {
      onMessageErrorKey(errorKey);
    }

    dispatch(SetIsLoading(false));
  }
);

export const signupByEmail = createAsyncThunk<void, IDefaultProps>(
  "registrationSlice/signupByEmail",
  async ({ setStep }, { dispatch, getState }) => {
    const { registrationSlice } = getState() as IRegistrationData;
    const { email, verify_token } = registrationSlice;

    if (!email) {
      onMessageError("Для регистрации требуется адрес электронной почты.");
      dispatch(
        SetErrors(["Для регистрации требуется адрес электронной почты."])
      );
      return;
    }

    if (!verify_token) {
      onMessageError("Для регистрации требуется токен подтверждения.");
      dispatch(SetErrors(["Для регистрации требуется токен подтверждения."]));
      return;
    }

    dispatch(SetErrors([]));
    dispatch(SetIsLoading(true));
    dispatch(SetSendedTo(SendToEnum.email));

    const response = await AuthService.$signup({
      email,
      send_to: SendToEnum.email,
      verify_token,
    });

    if (response.data.status) {
      onMessageSuccess("Код подтверждения отправлен на ваш email.");

      const { sended_to, reg_token } = response.data.values[0];

      dispatch(SetIsLoading(false));
      dispatch(SetReqToken(reg_token));
      dispatch(SetSendedToValue(sended_to));

      setStep(tabsNamesKeys.confirmCode);
    } else {
      onMessageErrorKey(response.data.errors[0]?.key);
    }

    dispatch(SetIsLoading(false));
  }
);

export const setAccountEmail = createAsyncThunk<void, IDefaultProps>(
  "registrationSlice/setAccountEmail",
  async ({ setStep }, { dispatch, getState }) => {
    const { registrationSlice } = getState() as IRegistrationData;
    const { email, _token } = registrationSlice;

    if (!email || !_token) {
      onMessageError("Произошла ошибка.");
      dispatch(SetErrors(["Произошла ошибка."]));
      return;
    }

    dispatch(SetErrors([]));
    dispatch(SetIsLoading(true));

    const response = await AuthService.$changeEmail({
      email,
      token: _token,
    });

    if (response.data.status) {
      onMessageSuccess("Email успешно сохранен.");
      dispatch(SetIsLoading(false));
      dispatch(SetSendedTo(SendToEnum.email));
      dispatch(SetSendedToValue(maskEmail(email)));

      setStep(tabsNamesKeys.confirmCode);
    } else {
      onMessageErrorKey(response.data.errors[0]?.key);
    }
    dispatch(SetIsLoading(false));
  }
);

export const confirmEmail = createAsyncThunk<void, IConfirmCodeProps>(
  "registrationSlice/confirmEmail",
  async ({ code, setStep }, { dispatch, getState }) => {
    const { registrationSlice } = getState() as IRegistrationData;
    const { _token } = registrationSlice;

    if (!code || !_token) {
      onMessageError("Произошла ошибка.");
      dispatch(SetErrors(["Произошла ошибка."]));
      return;
    }

    dispatch(SetErrors([]));
    dispatch(SetIsLoading(true));

    const response = await AuthService.$confirmEmail({
      code,
      token: _token,
    });

    if (response.data.status) {
      onMessageSuccess("Email успешно подтвержден.");
      dispatch(SetIsLoading(false));

      setStep(tabsNamesKeys.password);
    } else {
      onMessageErrorKey(response.data.errors[0]?.key);
    }

    dispatch(SetIsLoading(false));
  }
);

export const confirmSignup = createAsyncThunk<void, IConfirmCodeProps>(
  "registrationSlice/confirmSignup",
  async ({ code, setStep }, { dispatch, getState }) => {
    const { registrationSlice } = getState() as IRegistrationData;
    const {
      reg_token,
      sended_to,
      phone,
      email,
      passport,
      _isForeignPhone,
      _marketing_agreement,
    } = registrationSlice;
    const { name, sur_name, patronymic } = passport;

    if (!code || !name || !sur_name || !reg_token || !sended_to) {
      onMessageError("Произошла ошибка.");
      dispatch(SetErrors(["Произошла ошибка."]));
      return;
    }

    if (sended_to === SendToEnum.phone && !phone) {
      onMessageError("Нету номера телефона.");
      dispatch(SetErrors(["Нету номера телефона."]));
      return;
    }

    if (sended_to === SendToEnum.email && (!phone || !email)) {
      onMessageError("Нету номера телефона и почты.");
      dispatch(SetErrors(["Нету номера телефона и почты."]));
      return;
    }

    dispatch(SetErrors([]));
    dispatch(SetIsLoading(true));

    const response = await AuthService.$confirmSignup({
      code,
      phone: phone ?? "",
      email: email ?? "",
      reg_token,
      sended_to,
      passport: {
        name,
        sur_name,
        patronymic: patronymic || "",
      },
      marketing_agreement: _marketing_agreement,
    });

    if (response.data.status) {
      const [token, s3Token] = response.data.values;

      onMessageSuccess("Пользователь создан.");

      dispatch(SetToken(token));
      dispatch(SetS3Token(s3Token));

      if (!_isForeignPhone) {
        dispatch(SetIsLoading(false));
        setStep(tabsNamesKeys.email);
        return;
      }

      dispatch(SetIsLoading(false));
      setStep(tabsNamesKeys.password);
    } else {
      onMessageErrorKey(response.data.errors[0]?.key);
    }

    dispatch(SetIsLoading(false));
  }
);

export const resendCode = createAsyncThunk<void, IDefaultProps>(
  "registrationSlice/resendCode",
  async ({ setStep }, { dispatch, getState }) => {
    const { registrationSlice } = getState() as IRegistrationData;
    const { sended_to, reg_token } = registrationSlice;

    if (!sended_to || !reg_token) {
      onMessageError("Невозможно выслать код подтверждения.");
      dispatch(SetErrors(["Невозможно выслать код подтверждения."]));
      return;
    }

    dispatch(SetErrors([]));
    dispatch(SetIsLoading(true));

    dispatch(signupByPhone({ setStep }));

    dispatch(SetIsLoading(false));
  }
);

export const updatePassword = createAsyncThunk<void, { password: string }>(
  "registrationSlice/updatePassword",
  async ({ password }, { dispatch, getState }) => {
    const { registrationSlice } = getState() as IRegistrationData;
    const { _token, _s3Token } = registrationSlice;

    if (!password || !_token || !_s3Token) {
      onMessageError("Произошла ошибка.");
      dispatch(SetErrors(["Произошла ошибка."]));
      return;
    }

    dispatch(SetErrors([]));
    dispatch(SetIsLoading(true));

    const response = await AuthService.$updatePassword({
      password,
      token: _token,
    });

    if (response.data.status) {
      onMessageSuccess("Пароль успешно установлен.");
      dispatch(setUserTokens({ token: _token, s3Token: _s3Token }));
      redirect();
    } else {
      onMessageErrorKey(response.data.errors[0]?.key);
    }

    dispatch(SetIsLoading(false));
  }
);
